import { CircularProgress, useTheme } from '@mui/material';
import { useMemo, useState, useEffect } from 'react';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { BaseDrawerTitle, BaseDrawer } from '@mgm/common/components/base';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { getIsOpenPagesNav } from '../../../store/common/selectors';
import { commonActions } from '../../../store/common/slice';
import { getSelectedArticle } from '../../../store/help/selectors';
import { useArticles } from '../hooks';
import { helpActions } from '../../../store/help/slice';
import { TSelectedArticle } from '../../../types';
import 'react-lazy-load-image-component/src/effects/blur.css';

const ArticleNav = () => {
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const isOpenPagesNav = useAppSelector(getIsOpenPagesNav);
  const selectedArticle = useAppSelector(getSelectedArticle);
  const dispatch = useAppDispatch();
  const { articles } = useArticles();
  const { pageHeight, resizableDiv } = useScreenResize(59);
  const { t } = useTranslation('common');

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedArticle) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [selectedArticle]);

  const handleCloseNav = () => {
    dispatch(commonActions.setIsOpenPagesNav(false));

    setTimeout(() => {
      dispatch(helpActions.setSelectedArticle({} as TSelectedArticle));
    }, 400);
  };

  const currentArticle = useMemo(() => articles[selectedArticle?.articleId || 1], [selectedArticle.articleId]);

  const onCloseDrawer = () => {
    dispatch(commonActions.setToggleDrawer(false));
  };

  return (
    <BaseDrawer
      drawerProps={{
        anchor: 'right',
        width: isMobile ? '100%' : 600,
        variant: 'persistent',
      }}
      onCloseDrawer={onCloseDrawer}
      open={isOpenPagesNav}
    >
      <BaseDrawerTitle handleClose={handleCloseNav} title={currentArticle?.title} />
      <Divider sx={{ borderStyle: 'dashed' }} />
      <div ref={resizableDiv}>
        <Box
          mx={isMobile ? 3 : 6}
          my={3}
          sx={{
            height: pageHeight !== 0 ? pageHeight : 'auto',
          }}
        >
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
              {' '}
            </Box>
          ) : (
            <>
              <Typography>{currentArticle?.subtitle}</Typography>
              {Object.entries(currentArticle?.steps).map(([key, value], index) => (
                <div key={key}>
                  <Box display="flex" alignItems="center" mt={4} mb={3}>
                    <Badge badgeContent={index + 1} color="primary" />
                    <Typography
                      sx={{
                        px: 4,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: value.text,
                      }}
                    />
                  </Box>
                  {value.image && (
                  <LazyLoadImage
                    height={isMobile ? 200 : 300}
                    style={{
                      maxWidth: '100%',
                      width: isMobile ? '100%' : '100%',
                    }}
                    src={value.image}
                    alt="Article image"
                    effect="blur"
                  />
                  )}
                </div>
              ))}
              <Box py={4}>
                <Alert variant="filled" sx={{ bgcolor: '#FFC107', color: '#161C24' }} severity="info">
                  {t('stillHaveQuestions')}
                </Alert>
              </Box>
            </>
          )}
        </Box>
      </div>
    </BaseDrawer>
  );
};

export {
  ArticleNav,
};
